import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { productService } from '../services/productService';
import { getImageUrl } from '../config/constants';
import { Product } from '../types';

const BannerCarousel: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [bannerProducts, setBannerProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Fetch products with banners
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        setLoading(true);
        const products = await productService.getBanners();
        setBannerProducts(products);
      } catch (error) {
        console.error('Error fetching banners:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBanners();
  }, []);

  // Auto-rotate carousel every 5 seconds
  useEffect(() => {
    if (bannerProducts.length === 0) return;

    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % bannerProducts.length);
        setIsTransitioning(false);
      }, 300);
    }, 5000);

    return () => clearInterval(interval);
  }, [bannerProducts.length]);

  const goToSlide = (index: number) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex(index);
      setIsTransitioning(false);
    }, 300);
  };

  const goToPrevious = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev - 1 + bannerProducts.length) % bannerProducts.length);
      setIsTransitioning(false);
    }, 300);
  };

  const goToNext = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % bannerProducts.length);
      setIsTransitioning(false);
    }, 300);
  };

  const handleBannerClick = () => {
    if (bannerProducts[currentIndex]) {
      navigate(`/products/${bannerProducts[currentIndex].id}`);
    }
  };

  if (loading) {
    return (
      <section className="w-full relative bg-gray-100 h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-gray-900"></div>
      </section>
    );
  }

  if (bannerProducts.length === 0) {
    return null; // Don't render if no banners
  }

  const currentBanner = bannerProducts[currentIndex];

  return (
    <section className="w-full relative group">
      <div className="relative w-full overflow-hidden">
        {/* Carousel Slide */}
        <div className={`relative w-full flex items-center justify-center bg-white transition-all duration-500 ease-in-out ${
          isTransitioning ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
        }`}>
          {/* Banner Image - Clickable */}
          <div
            onClick={handleBannerClick}
            className="cursor-pointer w-full"
          >
            <img
              src={getImageUrl(currentBanner.banner_url || '')}
              alt={currentBanner.name}
              loading="lazy"
              className="w-full h-auto max-h-[60vh] object-cover"
            />
          </div>
        </div>

        {/* Navigation Arrows - Only show if more than one banner */}
        {bannerProducts.length > 1 && (
          <>
            <button
              onClick={goToPrevious}
              className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 w-14 h-14 md:w-16 md:h-16 rounded-full bg-white/80 hover:bg-white backdrop-blur-sm flex items-center justify-center text-gray-800 transition-all duration-300 hover:scale-110 shadow-xl opacity-90 hover:opacity-100"
              aria-label="Previous banner"
            >
              <svg className="w-7 h-7 md:w-8 md:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={goToNext}
              className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 w-14 h-14 md:w-16 md:h-16 rounded-full bg-white/80 hover:bg-white backdrop-blur-sm flex items-center justify-center text-gray-800 transition-all duration-300 hover:scale-110 shadow-xl opacity-90 hover:opacity-100"
              aria-label="Next banner"
            >
              <svg className="w-7 h-7 md:w-8 md:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}

        {/* Dots Indicator - Only show if more than one banner */}
        {bannerProducts.length > 1 && (
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
            {bannerProducts.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? 'bg-white w-8'
                    : 'bg-white/50 hover:bg-white/75'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default BannerCarousel;
